<template>
  <div class="translation-tool-text-display">
    <template v-for="(segment, index) in textSegments">
      <span
        v-if="!getTooltip(segment)"
        :key="index"
        :class="{
          'clickable-term': segment.isClickable,
          'ignored-term': segment.isIgnored
        }"
        @click="handleSegmentClick(segment)"
      >{{ segment.text }}</span>
      <span
        v-else
        :key="'no-tooltip-'+index"
        :class="{
          'clickable-term': segment.isClickable,
          'ignored-term': segment.isIgnored
        }"
        @click="handleSegmentClick(segment)"
        v-tooltip="getTooltip(segment)"
      >{{ segment.text }}</span>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'TranslationToolTextDisplay',
  props: {
    text: {
      type: String,
      required: true
    },
    results: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapState('app', [
      'translationToolIgnoreList',
      'ignoreStandardParticles',
      'standardParticles',
      'particleCategoriesEnabled'
    ]),
    textSegments() {
      if (!this.text || !this.results || this.results.length === 0) {
        return [{ text: this.text || '', isClickable: false, isIgnored: false }];
      }

      const segments = [];
      let currentIndex = 0;
      const textLength = this.text.length;

      // Sort results by position and length (longer matches first to handle overlaps)
      const sortedResults = [...this.results]
        .filter(r => r.subsearchLevel === 0) // Only main search results, not subsearches
        .sort((a, b) => {
          const aText = this.cleanTibetan(a.tibetan);
          const bText = this.cleanTibetan(b.tibetan);
          // Sort by length descending to prefer longer matches
          return bText.length - aText.length;
        });

      // Find all occurrences of result terms in the text
      const matches = [];
      for (const result of sortedResults) {
        const termText = this.cleanTibetan(result.tibetan);
        const isIgnored = this.isTermIgnored(result.tibetan);

        let searchStart = 0;
        while (searchStart < textLength) {
          const pos = this.text.indexOf(termText, searchStart);
          if (pos === -1) break;

          // Check if this position overlaps with an existing match
          const overlaps = matches.some(m =>
            (pos >= m.start && pos < m.end) ||
            (pos + termText.length > m.start && pos < m.end)
          );

          if (!overlaps) {
            matches.push({
              start: pos,
              end: pos + termText.length,
              text: termText,
              result: result,
              isIgnored: isIgnored
            });
          }

          searchStart = pos + 1;
        }
      }

      // Sort matches by position
      matches.sort((a, b) => a.start - b.start);

      // Build segments
      for (const match of matches) {
        // Add any text before this match
        if (currentIndex < match.start) {
          segments.push({
            text: this.text.substring(currentIndex, match.start),
            isClickable: false,
            isIgnored: false
          });
        }

        // Add the matched term
        segments.push({
          text: match.text,
          isClickable: !match.isIgnored,
          isIgnored: match.isIgnored,
          result: match.result,
          resultIndex: this.results.findIndex(r => r === match.result)
        });

        currentIndex = match.end;
      }

      // Add any remaining text
      if (currentIndex < textLength) {
        segments.push({
          text: this.text.substring(currentIndex),
          isClickable: false,
          isIgnored: false
        });
      }

      return segments;
    }
  },
  methods: {
    cleanTibetan(text) {
      // Remove brackets and trailing tshegs/shads like in the Definition component
      return text
        .replace(/\[.*\]$/gm, '')
        .replace(/[་།]+$/g, '');
    },
    normalizeTibetan(text) {
      // Normalize by removing spaces and formatting
      return text.replace(/\s+/g, '').replace(/[་།]+$/g, '');
    },
    isTermIgnored(tibetanText) {
      const normalizedTerm = this.normalizeTibetan(tibetanText);

      // Check custom ignore list
      const isInCustomList = this.translationToolIgnoreList.some(ignoredTerm => {
        return this.normalizeTibetan(ignoredTerm) === normalizedTerm;
      });

      if (isInCustomList) return true;

      // Check standard particles if enabled
      if (this.ignoreStandardParticles) {
        for (const category of this.standardParticles) {
          if (this.particleCategoriesEnabled[category.id]) {
            const isInCategory = category.particles.some(particle => {
              return this.normalizeTibetan(particle) === normalizedTerm;
            });
            if (isInCategory) return true;
          }
        }
      }

      return false;
    },
    getSmallResult(result) {
      const fields = [
        'english1', 'rangjung_english', 'berzin', 'english2', 'eightfourthousand_dict', 'tsepak_rigdzin', 
        'english_others', 'hopkins_2015',
        'synonyms_english', 'definition_english', 'divisions_english',
        'example_english1', 'eightfourthousand_def', 'comment', 'berzin_english', 'tshig_mdzod_chen_mo',
        'dag_tshig_gsar_bsgrigs', 'dung_dkar_tshig_mdzod_chen_mo'
      ];

      for (const field of fields) {
        if (result[field] && result[field] !== '') {
          // Truncate if too long
          const text = result[field];
          return text.length > 100 ? text.substring(0, 100) + '...' : text;
        }
      }
      return '';
    },
    getTooltip(segment) {
      if (!segment) return null;

      if (!segment.isClickable && !segment.isIgnored) {
        return null;
      }

      if (segment.isIgnored) {
        return 'Ignored term';
      }

      if (segment.result) {
        const preview = this.getSmallResult(segment.result);
        return preview || 'Click to view definition';
      }

      return null;
    },
    handleSegmentClick(segment) {
      if (!segment.isClickable || segment.isIgnored) {
        return;
      }

      // Emit event to parent to scroll to result
      if (segment.resultIndex !== undefined) {
        this.$emit('term-clicked', segment.resultIndex);
      }
    }
  }
};
</script>

<style scoped>
.translation-tool-text-display {
  padding: 10px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1.7rem;
  line-height: 1.6;
  min-height: 90px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.clickable-term {
  cursor: pointer;
  transition: background-color 0.2s ease, text-decoration 0.2s ease;
}

.clickable-term:hover {
  background-color: #f0f8ff;
  text-decoration: underline;
}

.ignored-term {
  cursor: default;
  opacity: 0.6;
}
</style>
